import React, { useState, useEffect, useCallback } from 'react';
import { FoodProvider, useFoodContext } from './context';
import { Header, Navigation, MedicalDisclaimer, PWANotifications } from './components';
import SearchPage from './pages/SearchPage';
import OpenFoodFactsPage from './pages/OpenFoodFactsPage';
import CalculatorPage from './pages/CalculatorPage';
import AddFoodPage from './pages/AddFoodPage';
import GuidePage from './pages/GuidePage';
import SyncPage from './pages/SyncPage';
import RecipesPage from './pages/RecipesPage';
import RecipeViewPage from './pages/RecipeViewPage';
import PantryPage from './pages/PantryPage';
import { TABS } from './utils/constants';

// Parse hash like #recipe/42 → { tab: TABS.RECIPE_VIEW, recipeId: 42 }
// or #pantry → { tab: TABS.PANTRY }
const parseHash = () => {
  const hash = window.location.hash.slice(1); // strip leading #
  const recipeMatch = hash.match(/^recipe\/(\d+)$/);
  if (recipeMatch) return { tab: TABS.RECIPE_VIEW, recipeId: parseInt(recipeMatch[1], 10) };
  const tabMap = {
    recipes: TABS.RECIPES,
    pantry: TABS.PANTRY,
    search: TABS.SEARCH,
    calculator: TABS.CALCULATOR,
    guide: TABS.GUIDE,
  };
  if (tabMap[hash]) return { tab: tabMap[hash], recipeId: null };
  return null;
};

const AppContent = () => {
  const { activeTab, setActiveTab } = useFoodContext();
  const [activeRecipeId, setActiveRecipeId] = useState(null);

  // On mount: restore state from hash
  useEffect(() => {
    const parsed = parseHash();
    if (parsed) {
      setActiveTab(parsed.tab);
      if (parsed.recipeId) setActiveRecipeId(parsed.recipeId);
    }
  }, [setActiveTab]);

  // Sync hash when tab/recipe changes
  useEffect(() => {
    if (activeTab === TABS.RECIPE_VIEW && activeRecipeId) {
      window.location.hash = `recipe/${activeRecipeId}`;
    } else if (activeTab === TABS.RECIPES) {
      window.location.hash = 'recipes';
    } else if (activeTab === TABS.PANTRY) {
      window.location.hash = 'pantry';
    } else {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [activeTab, activeRecipeId]);

  // Handle browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      const parsed = parseHash();
      if (parsed) {
        setActiveTab(parsed.tab);
        if (parsed.recipeId) setActiveRecipeId(parsed.recipeId);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [setActiveTab]);

  const handleOpenRecipe = useCallback((id) => {
    setActiveRecipeId(id);
    setActiveTab(TABS.RECIPE_VIEW);
  }, [setActiveTab]);

  const renderCurrentPage = () => {
    switch (activeTab) {
      case TABS.SEARCH:         return <SearchPage />;
      case TABS.OPEN_FOOD_FACTS: return <OpenFoodFactsPage />;
      case TABS.CALCULATOR:     return <CalculatorPage />;
      case TABS.ADD:            return <AddFoodPage />;
      case TABS.RECIPES:        return <RecipesPage onOpenRecipe={handleOpenRecipe} />;
      case TABS.RECIPE_VIEW:    return <RecipeViewPage recipeId={activeRecipeId} />;
      case TABS.PANTRY:         return <PantryPage />;
      case TABS.GUIDE:          return <GuidePage />;
      case TABS.SYNC:           return <SyncPage />;
      default:                  return <SearchPage />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Header />
        <Navigation />
        {renderCurrentPage()}
        <MedicalDisclaimer />
      </div>
    </div>
  );
};

const App = () => (
  <FoodProvider>
    <AppContent />
    <PWANotifications />
  </FoodProvider>
);

export default App;